import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { Modal, ModalOptions } from '@bazis/shared/models/modal.types';

/**
 * **BazisModalService**
 *
 * Manages a stack/list of modal dialogs.
 * Provides helpers to create a modal with merged default options and to dismiss
 * the last or a specific modal instance.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisModalService {
    /**
     * Default options applied to every modal.
     * Values provided to `create()` override these defaults.
     */
    private _defaultParams: ModalOptions = {
        component: null,
        componentProperties: null,
        hasCloseIcon: true,
        hasBackdrop: true,
        backdropClose: true,
        keyboardClose: true,
        modalType: 'default',
        cssClass: '',
        id: '',
    };

    /**
     * Observable holder for active modals list.
     * The array order is the creation order (last item is the topmost modal).
     */
    modals = new TemplateObservable([]);

    /**
     * Create and register a new modal.
     *
     * The modal id is generated with `uuidv4()` when `params.id` is not provided.
     * Supports passing `injector` and `environmentInjector` to provide dependencies for modal content.
     *
     * The created modal exposes `onDidDismiss()` Promise that resolves when `dismiss()` is called.
     *
     * @param params - Partial modal options to apply.
     * @returns Created `Modal` instance.
     */
    create(params: Partial<ModalOptions>) {
        const modalParams: ModalOptions = {
            ...this._defaultParams,
            ...params,
            id: params.id || uuidv4(),
            injector: params.injector ?? null,
            environmentInjector: params.environmentInjector ?? null,
        };

        const modal: Modal = {
            options: modalParams,
            onDidDismiss: () => {
                return new Promise((resolve) => {
                    modal.dismiss = resolve;
                });
            },
        };

        const modals = this.modals._;
        modals.push(modal);

        this.modals.set(modals);

        return modal;
    }

    /**
     * Dismiss a modal and remove it from `modals`.
     *
     * If `modal` is not provided, dismisses the last modal in the list.
     * If there is no modal to dismiss, the method returns immediately.
     *
     * @param result - Value passed to the modal `dismiss()` callback. Default: `null`.
     * @param modal - Modal instance to dismiss. Default: `null` (dismiss last).
     * @returns void
     */
    dismiss(result = null, modal = null) {
        let modals = this.modals._;
        if (!modal && modals.length > 0) modal = modals[modals.length - 1];

        if (!modal) return;
        if (modal.dismiss) modal.dismiss(result);
        modals = modals.filter((v) => v.options.id !== modal.options.id);
        this.modals.set(modals);
    }
}
